import { contactFournisseur, decouperAdresses, uniteLisible } from './relances.js';
import { modeleMail } from './modeles-mail.js';
const MODELES = {
    fr: {
        objet: (ref, client) => `Nouvelle Commande ${ref} - ${client}`,
        entete: 'Bonjour,\nNous souhaitons passer une commande pour les produits suivants :',
        adresse: 'Adresse de livraison :',
        telephone: 'Tel',
        politesse: 'Nous restons disponibles si besoin,\nMerci à vous',
        aCompleter: (quoi) => `[À COMPLÉTER — ${quoi}]`,
        aucunProduit: '[À COMPLÉTER — aucun produit sur la commande]',
        objetRepasse: (ref, client) => `RELANCE — Commande ${ref} - ${client} (sans trace chez vous)`,
        enteteRepasse: (ref) => 'Bonjour,\n'
            + `Nous revenons vers vous au sujet de notre commande ${ref}, que nous vous avons déjà\n`
            + "transmise et qui n'apparaît pas dans votre système.\n\n"
            + 'Il ne s\'agit donc pas d\'une nouvelle commande : nous vous en retransmettons ci-dessous\n'
            + 'tous les éléments, à l\'identique.',
        urgenceRepasse: 'Merci de nous confirmer sa bonne réception et de la traiter en priorité : le délai qui\n'
            + 'court maintenant s\'ajoute à celui déjà écoulé depuis notre première demande, et notre\n'
            + 'client attend toujours.',
    },
    en: {
        objet: (ref, client) => `New Order ${ref} - ${client}`,
        entete: 'Hello,\nWe would like to place an order for the following products:',
        adresse: 'Delivery address:',
        telephone: 'Phone',
        politesse: 'We remain available if needed,\nThank you',
        aCompleter: (quoi) => `[TO COMPLETE — ${quoi}]`,
        aucunProduit: '[TO COMPLETE — no product on this order]',
        objetRepasse: (ref, client) => `FOLLOW-UP — Order ${ref} - ${client} (no trace on your side)`,
        enteteRepasse: (ref) => 'Hello,\n'
            + `We are contacting you again about our order ${ref}, which we already sent you and\n`
            + 'which does not appear in your system.\n\n'
            + 'This is therefore NOT a new order: we are sending you all of its details again below,\n'
            + 'unchanged.',
        urgenceRepasse: 'Please confirm that you have received it and give it priority: the time it takes from\n'
            + 'now adds to the delay already incurred since our first request, and our customer is\n'
            + 'still waiting.',
    },
};
/**
 * Bloc d'adresse, en texte.
 *
 * L'adresse structurée quand elle existe, sinon le bloc Sellsy. Le scénario
 * choisissait entre les deux d'après la LONGUEUR de la référence de commande
 * (`> 8` ou `== 5`) ; on regarde plutôt si l'adresse structurée porte quelque
 * chose, ce qui donne le même résultat sur les données réelles — les commandes
 * Sellsy n'ont ni ligne ni ville — sans dépendre d'un format de référence.
 */
/**
 * Bloc « adresse de livraison », partagé avec la relance.
 *
 * Exporté parce qu'une relance sur une commande que le fournisseur n'a jamais
 * enregistrée doit porter TOUT ce que portait la commande — adresse comprise,
 * sans quoi il ne peut rien en faire.
 */
export function blocAdresseLivraison(d, m) {
    const a = d.adresse;
    const structuree = [a.ligne, a.ville].some((v) => String(v ?? '').trim());
    if (!structuree) {
        const brut = sansMentionAdresse(nettoyerTexte(String(a.detailsLivraison ?? '')));
        return brut ? `${m.adresse}\n${brut}` : '';
    }
    const villeEtCode = [a.ville, a.codePostal].filter((v) => String(v ?? '').trim()).join(' - ');
    const lignes = [
        d.entreprise,
        d.client,
        a.ligne,
        a.complement,
        villeEtCode,
        a.pays,
        d.telephone ? `${m.telephone} : ${d.telephone}` : null,
    ].filter((l) => String(l ?? '').trim());
    return `${m.adresse}\n${lignes.join('\n')}`;
}
/**
 * Retire la mention « Adresse de livraison » du bloc Sellsy.
 *
 * Sellsy l'écrit en tête de ses détails de livraison, et le mail pose déjà la
 * sienne juste au-dessus : sans ce retrait, le fournisseur la lisait deux fois.
 * Retirée où qu'elle soit, et pas seulement seule sur sa ligne — « Adresse de
 * livraison : 12 rue… » garde sa rue. Une ligne qui ne portait qu'elle
 * disparaît.
 */
export function sansMentionAdresse(texte) {
    return texte
        .split('\n')
        .map((l) => l.replace(/adresse\s+de\s+livraison\s*:?\s*/gi, '').trim())
        .filter(Boolean)
        .join('\n');
}
/**
 * Nettoie un champ venu d'ailleurs.
 *
 * `detailsLivraison` arrive tantôt en texte brut à retours à la ligne, tantôt
 * avec des balises héritées de Sellsy. Les `<br>` deviennent des retours, le
 * reste du balisage est retiré — l'échappement, lui, aura lieu à la mise en
 * HTML, une fois le texte relu.
 */
function nettoyerTexte(brut) {
    return brut
        .replace(/<br\s*\/?>/gi, '\n')
        .replace(/<[^>]*>/g, '')
        .replace(/\r\n/g, '\n')
        .split('\n')
        .map((l) => l.trim())
        .filter(Boolean)
        .join('\n');
}
/**
 * Compose le mail de commande d'UN fournisseur.
 *
 * Fonction pure : la langue et les adresses viennent des réglages, le reste de
 * la commande. Une société absente de l'annuaire ressort sans destinataire —
 * l'appelant refuse alors l'envoi plutôt que d'inventer une adresse.
 */
export function composerCommandeFournisseur(reglages, d) {
    const contact = contactFournisseur(reglages, d.societe);
    const m = MODELES[contact.langue];
    // Une ligne sans correspondance ne disparaît pas : elle devient un marqueur
    // visible, avec sa référence et sa quantité. Écrire notre désignation interne
    // ne servirait à rien — le fournisseur ne la reconnaît pas —, mais la faire
    // disparaître serait pire : on enverrait une commande amputée sans que rien
    // ne le montre dans le texte qu'on relit.
    const manquants = [];
    const lignes = [];
    for (const a of d.articles) {
        const reference = String(a.reference ?? '').trim();
        // « 12 m² », pas « 12m² » : l'espace sépare le nombre de son unité.
        const quantite = a.quantite === null || a.quantite === undefined ? '' : `${a.quantite} `;
        const suffixe = ` : ${quantite}${uniteLisible(a.unite)}`;
        if (a.correspondance) {
            lignes.push(`- ${a.correspondance}${suffixe}`);
            continue;
        }
        manquants.push(reference || '(référence absente)');
        lignes.push(`- ${m.aCompleter(reference || '(référence absente)')}${suffixe}`);
    }
    // Aucun produit du tout : le mail s'ouvre quand même, avec une ligne à
    // remplacer. Refuser laisserait l'utilisateur sans aucun moyen d'avancer.
    if (!lignes.length)
        lignes.push(`- ${m.aucunProduit}`);
    /*
     * Le texte vient du registre des modèles, donc de Réglages.
     *
     * Il vivait ici, en fragments — en-tête, politesse, urgence — assemblés dans
     * un ordre fixe : le corriger demandait un déploiement, et rien à l'écran ne
     * disait que ce mail-là n'était pas modifiable alors que la relance l'était.
     *
     * Les variables `{{articles}}` et `{{adresse}}` portent ce que le code sait
     * construire et qu'un texte ne peut pas : les lignes en désignation
     * fournisseur, et le bloc d'adresse mis en forme.
     */
    const modele = modeleMail(reglages, d.repasse ? 'commande_repassee' : 'commande_fournisseur', contact.langue);
    const valeurs = {
        '{{ref}}': d.ref,
        '{{client}}': d.client,
        '{{fournisseur}}': d.societe || '',
        '{{ville}}': d.adresse?.ville ?? '—',
        '{{articles}}': lignes.join('\n'),
        '{{adresse}}': blocAdresseLivraison(d, m),
    };
    const remplir = (texte) => Object.entries(valeurs).reduce((acc, [cle, v]) => acc.split(cle).join(v), texte);
    const corps = remplir(modele.corps).replace(/\n{3,}/g, '\n\n').trim();
    return {
        langue: contact.langue,
        destinataires: contact.email,
        objet: remplir(modele.objet),
        corps,
        contactConnu: contact.connu && decouperAdresses(contact.email).length > 0,
        manquants,
        sansProduit: d.articles.length === 0,
    };
}
//# sourceMappingURL=commande-fournisseur.js.map